import React, {useState} from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  SimpleGrid,
  Spacer,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {useKeyplayApi} from '../../../context/ApiContext';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {ApiKeyResponse} from '../../../shared/api/api';
import {CustomerOptions} from '../../../shared/customer';
import {Copy, TrashCan, Api as ApiIcon} from '@carbon/icons-react';
import {DeleteApiKeyModal} from './DeleteApiKeyModal';
import {IntentSourcesInfo} from './IntentSourcesInfo';
import {CreateIntentSourceButton} from './CreateIntentSourceModal';
import {
  useIsFeatureEnabled,
  useIsFreeOrListBuilderCustomer,
  useCustomer,
} from '../../../hooks/api/metadata';
import {ObjectId} from 'bson';
import {ApiKey} from '../../../shared/api';
import {maskString} from '../../../lib/helpers';
import {useGetApiKeys, useCreateApiKey} from '../../../hooks/api/apiKeys';

const columnHelper = createColumnHelper<ApiKey>();

const ApiKeyInfo = ({keys}: {keys: ApiKeyResponse}) => {
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();
  const toast = useToast();

  const [apiKeyIdToDelete, setApiKeyIdToDelete] = useState<ObjectId>();

  const columns = [
    columnHelper.accessor('key', {
      cell: (info) => {
        return maskString({
          str: info.getValue(),
          visibleLength: 8,
          maskLength: 18,
        });
      },
      header: 'Key',
      id: 'key',
    }),
    columnHelper.accessor('key', {
      header: '',
      id: 'button',
      cell: (info) => {
        const key = info.getValue();
        return (
          <Button
            key="generate"
            fontWeight="500"
            fontSize="14"
            colorScheme="kbuttonblue"
            variant="outline"
            onClick={() => {
              toast({
                status: 'success',
                title: 'Key copied to clipboard.',
                duration: 2_000,
                isClosable: true,
              });
              navigator.clipboard.writeText(key);
            }}
          >
            <Copy />
          </Button>
        );
      },
    }),
    columnHelper.accessor('generated', {
      cell: (info) => info.getValue().toLocaleString(),
      id: 'generated',
      header: 'Generated',
    }),
    columnHelper.accessor('lastUsed', {
      cell: (info) => {
        const lastUsed = info.getValue();
        if (!lastUsed) {
          return '\u2014';
        }

        return lastUsed.toLocaleString();
      },
      id: 'lastused',
      header: 'Last Used',
    }),
    columnHelper.accessor('_id', {
      header: '',
      id: 'delete',
      cell: (info) => {
        const id = info.getValue();
        return (
          <Button
            key="delete"
            fontWeight="500"
            fontSize="14"
            colorScheme="red"
            variant="outline"
            onClick={() => {
              setApiKeyIdToDelete(id);
              onDeleteModalOpen();
            }}
          >
            <TrashCan />
          </Button>
        );
      },
    }),
  ];

  const table = useReactTable({
    data: keys,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <DeleteApiKeyModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        apiKeyIdToDelete={apiKeyIdToDelete}
      />
      <TableContainer>
        <Table variant="simple">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th key={header.id} isNumeric={header.column.id === 'delete'}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td key={cell.id} isNumeric={cell.column.id === 'delete'}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </>
  );
};

const ApiKeys = () => {
  const makeApiCall = useKeyplayApi();
  const apiKey = useIsFeatureEnabled('apiKey');
  const intent = useIsFeatureEnabled('intent');
  const customer = useCustomer();
  const apiIntentSources =
    customer.intentSources?.filter((source) => source.type === 'api') ?? [];
  const {data: apiKeys, isSuccess: apiKeysIsSuccess} = useGetApiKeys({});
  const {data: options, isSuccess: optionsIsSuccess} = useQuery({
    queryKey: ['options'],
    queryFn: () => makeApiCall<CustomerOptions>('/options'),
  });

  const queryClient = useQueryClient();
  const toast = useToast();
  const createApiKey = useCreateApiKey();

  const addOnEnrich = useMutation<void, unknown, {value: boolean}>({
    mutationFn: ({value}) => {
      return makeApiCall(
        '/options/addOnEnrich',
        {
          method: 'POST',
          data: {value},
        },
        {toastOnError: true}
      );
    },
    onMutate: async ({value: addOnEnrich}) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({queryKey: ['options']});

      // Optimistically update to the new value
      queryClient.setQueryData<CustomerOptions>(['options'], (old) => ({
        ...(old || {}),
        addOnEnrich,
      }));
    },
    onSuccess: () => {
      toast({
        status: 'success',
        title: 'Configuration updated.',
        duration: 2_000,
        isClosable: true,
      });
    },
    // Always refetch after error or success.
    onSettled: async () => {
      await queryClient.invalidateQueries({queryKey: ['options']});
    },
  });

  return (
    <>
      {(apiKey || (apiKeysIsSuccess && apiKeys.length > 0)) && (
        <>
          <Box
            borderColor="#e8e8e8"
            borderWidth="1px"
            borderRadius="8px"
            p={6}
            mt={8}
          >
            <Box fontWeight={500} pl={4} display="flex" alignItems="center">
              Settings
            </Box>
            <Box pl={4} pt={8}>
              <SimpleGrid columns={2} gridTemplateColumns="min-content auto">
                <Checkbox
                  alignItems="top"
                  pt={0.5}
                  isChecked={options?.addOnEnrich}
                  onChange={(e) =>
                    addOnEnrich.mutate({value: e.target.checked})
                  }
                  isDisabled={!optionsIsSuccess}
                />
                <Box pl={4}>
                  <Box fontWeight={500}>
                    When a new account is received via the API, add it to
                    Keyplay Active Accounts
                  </Box>
                  <Box pt={2} fontStyle="italic">
                    We recommend turning this ON if you are using the API to
                    enrich your CRM account objects. For any other use cases
                    this setting should be OFF.
                  </Box>
                </Box>
              </SimpleGrid>
            </Box>
          </Box>
          <Box
            borderColor="#e8e8e8"
            borderWidth="1px"
            borderRadius="8px"
            p={6}
            mt={8}
          >
            <Flex>
              <Box fontWeight={500} pl={4} display="flex" alignItems="top">
                Keys
              </Box>
              <Spacer />
              {apiKey && (
                <Button
                  key="approve"
                  fontWeight="500"
                  fontSize="14"
                  mr={4}
                  colorScheme="kbuttonblue"
                  onClick={() =>
                    createApiKey.mutate(
                      {},
                      {
                        onSuccess: () => {
                          toast({
                            status: 'success',
                            title: 'API key created.',
                            duration: 2_000,
                            isClosable: true,
                          });
                        },
                      }
                    )
                  }
                  isLoading={createApiKey.isPending}
                >
                  Create
                </Button>
              )}
            </Flex>
            <Box mt="8">
              {apiKeysIsSuccess ? (
                apiKeys.length ? (
                  <ApiKeyInfo keys={apiKeys} />
                ) : apiKey ? (
                  <Box margin="auto" pt={4} pl={4}>
                    <i>No API keys found, try generating one.</i>
                  </Box>
                ) : (
                  <></>
                )
              ) : (
                <></>
              )}
            </Box>
          </Box>
        </>
      )}
      {apiKey && intent && (
        <Box
          borderColor="#e8e8e8"
          borderWidth="1px"
          borderRadius="8px"
          p={6}
          mt={8}
        >
          <Flex justifyContent="space-between">
            <Box fontWeight={500} pl={4} display="flex" alignItems="top">
              Intent Sources
            </Box>
            <CreateIntentSourceButton mr={4} />
          </Flex>
          <Box mt="8">
            {apiIntentSources.length > 0 ? (
              <IntentSourcesInfo sources={apiIntentSources} />
            ) : (
              <Box margin="auto" pt={4} pl={4}>
                <i>No intent sources found, try creating one.</i>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

const Api = () => {
  const isFreemiumOrListBuilderCustomer = useIsFreeOrListBuilderCustomer();

  return (
    <Flex direction="column">
      <Box>
        <Box fontSize={20} fontWeight={500}>
          Keyplay API
        </Box>
        <br />
        <Box>
          The Keyplay enrichment API will return signals and scores for any
          domain. The API can be integrated with iPaaS tools like Zapier or Make
          to enrich inbound leads in your CRM or MAP.
        </Box>
      </Box>

      {isFreemiumOrListBuilderCustomer ? (
        <Flex
          mt={6}
          p={8}
          borderWidth="1px"
          borderRadius="8px"
          borderColor="#e8e8e8"
          bg="kgray.100"
          direction="column"
          align="center"
          justify="center"
          textAlign="center"
          minH="200px"
        >
          <Box mb={4} color="kred.300">
            <ApiIcon size={32} />
          </Box>
          <Box fontSize="lg" fontWeight="medium" mb={2}>
            Unlock API Access
          </Box>
          <Box color="kgray.300" maxW="400px">
            Integrate Keyplay data directly into your workflows with our REST
            API. Available on annual plans.
          </Box>
          <Button
            as="a"
            href="https://keyplay.io/pricing/"
            target="_blank"
            mt={6}
            colorScheme="kbuttonblue"
            size="md"
          >
            View Pricing
          </Button>
        </Flex>
      ) : (
        <ApiKeys />
      )}
    </Flex>
  );
};

export default Api;
